// Public Agreement Signing Page (no auth required)
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Scale, FileText, CheckCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignAgreementPage() {
  const { agreementId } = useParams();
  const [loading, setLoading] = useState(true);
  const [agreement, setAgreement] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAgreement();
  }, [agreementId]);

  const fetchAgreement = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/agreements/${agreementId}/public`);
      if (res.ok) {
        setAgreement(await res.json());
      } else {
        const data = await res.json();
        setError(data.detail || 'Agreement not found');
      }
    } catch (error) {
      setError('Failed to load agreement');
    } finally {
      setLoading(false);
    }
  };

  const signAgreement = async () => {
    if (!agreed) {
      toast.error('Please confirm that you agree to the terms');
      return;
    }
    if (!signerName.trim()) {
      toast.error('Please enter your full legal name');
      return;
    }

    setSigning(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/agreements/${agreementId}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agreement_confirmed: true,
          signer_name: signerName.trim()
        })
      });

      if (res.ok) {
        setSigned(true);
        toast.success('Agreement signed successfully!');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to sign agreement');
      }
    } catch (error) {
      toast.error('Failed to sign agreement');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-center">
          <FileText className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading agreement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Unable to Load Agreement</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Agreement Signed!</h2>
            <p className="text-muted-foreground mb-4">
              Thank you for signing the Legal Services Agreement. 
              You will receive a confirmation email with a copy of the signed agreement.
            </p>
            <p className="text-sm text-muted-foreground">
              You may now close this window.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="sign-agreement-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-navy flex items-center justify-center">
            <Scale className="w-6 h-6 text-white" />
          </div>
          <div>
            <span className="font-heading text-xl font-bold text-navy">Legal AI</span>
            <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Legal Services Agreement</CardTitle>
            <p className="text-muted-foreground">
              Please review the agreement below carefully before signing.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Agreement Content */}
            <div className="bg-slate-50 rounded-lg p-6 max-h-[500px] overflow-y-auto">
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-sm">
                  {agreement?.terms_content}
                </pre>
              </div>
            </div>

            {/* Signing Section */}
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-4">Electronic Signature</h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Your Full Legal Name *</Label>
                  <Input
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Enter your full legal name"
                    className="max-w-md"
                  />
                </div>

                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <Checkbox
                    id="agree"
                    checked={agreed}
                    onCheckedChange={setAgreed}
                    className="mt-1"
                  />
                  <Label htmlFor="agree" className="text-sm cursor-pointer">
                    I have read and understand the terms of this Legal Services Agreement. 
                    I agree to be bound by its terms and conditions. I understand that by 
                    clicking "Sign Agreement" below, I am creating a legally binding electronic 
                    signature that has the same legal effect as a handwritten signature.
                  </Label>
                </div>

                <div className="flex items-center justify-between pt-4">
                  <p className="text-xs text-muted-foreground">
                    Your IP address and timestamp will be recorded for verification.
                  </p>
                  <Button
                    onClick={signAgreement}
                    disabled={!agreed || !signerName.trim() || signing}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {signing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Sign Agreement
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
